import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import SearchIcon from '@mui/icons-material/Search';
import { Grid, Box, Paper, Typography } from '@mui/material';

import { Modal, Button, TextField } from 'shared';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { marketplaces } from '../../utils/paymentsTableHeaders';
import { useRemittanceService } from 'store/services/remittance.service';
import { paymentActionModalStyles } from 'components/Invoice/styles/styles';

const PaymentActionModal = ({
    data,
    openModal,
    setOpenModal,
    setAlertData,
    handlePaymentModalSuccess,
}) => {
    const classes = paymentActionModalStyles();
    const { skipRemittance, matchRemittance } = useRemittanceService();
    const companyId = useSelector((state) => state?.company?.data?.id);

    const [invoiceId, setInvoiceId] = useState('');
    const [skipLoading, setSkipLoading] = useState(false);
    const [matchLoading, setMatchLoading] = useState(false);
    const [showMatchCard, setShowMatchCard] = useState(false);

    const handleSkipClick = async () => {
        try {
            setSkipLoading(true);
            const response = await skipRemittance({ company_id: companyId, id: data?.id });
            handlePaymentModalSuccess(response.message);
        } catch (error) {
            handleErrorAlert(setAlertData, undefined, error);
        } finally {
            setSkipLoading(false);
        }
    };

    const handleMatchClick = async () => {
        try {
            setMatchLoading(true);
            const response = await matchRemittance({
                company_id: companyId,
                id: data?.id,
                invoiceId,
            });
            handlePaymentModalSuccess(response.message);
        } catch (error) {
            handleErrorAlert(setAlertData, undefined, error);
        } finally {
            setMatchLoading(false);
        }
    };

    return (
        <Modal
            fullWidth={true}
            open={openModal}
            title="Invoice Payment Details"
            handleClose={() => setOpenModal(false)}
            customWidth="50%"
            showFooter
            footer={
                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        customStyles={classes.cancelButton}
                        onClick={() => setOpenModal(false)}
                        disabled={skipLoading || matchLoading}
                        text="Close"
                    />
                </Box>
            }
        >
            <Paper sx={classes.summaryWrapper}>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            PAID ON
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {data?.payment_date || '—'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            MARKETPLACE
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {marketplaces[data?.marketplace] || '—'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            INVOICE ID
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {data?.remittance_invoice_id ||
                                `Amount Paid on ${data?.payment_date}: $${data?.payment_amount}`}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            REMITTANCE
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {data?.remittance_id || '—'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            TRANSACTION TYPE
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {data?.transaction_type || 'Payment'}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                            AMOUNT
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                            {`$${data?.payment_amount}` || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {data?.status === 'skipped' && !showMatchCard ? (
                <Paper sx={classes.actionWrapper}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        <Typography variant="subtitle1" color="textSecondary">
                            POS MATCHING SKIPPED
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={classes.findMatchBox}
                            onClick={() => setShowMatchCard(true)}
                        >
                            <SearchIcon fontSize="small" />
                            <Typography variant="body1" fontWeight="bold">
                                FIND A MATCH
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            ) : (
                <Paper sx={classes.actionWrapper}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                                POS Invoice ID
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Id of the invoice in your POS"
                                size="small"
                                value={invoiceId}
                                onChange={(e) => {
                                    setInvoiceId(e.target.value);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button
                                text="MATCH"
                                color="primary"
                                variant="contained"
                                loading={matchLoading}
                                onClick={handleMatchClick}
                                customStyles={classes.matchButton}
                                disabled={skipLoading || !invoiceId}
                            />
                            <Button
                                variant="text"
                                text="SKIP MATCH"
                                loading={skipLoading}
                                disabled={matchLoading}
                                onClick={handleSkipClick}
                                customStyles={classes.skipButton}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}
        </Modal>
    );
};

export default PaymentActionModal;
